/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button, Grid, Stack, SvgIcon, useMediaQuery, useTheme, Tabs, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ManageRetailersBox from './components/ManageRetailersBox';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useContext, useEffect, useState } from 'react';
import useLogError from '../../../hooks/useLogError';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Plus } from '../../../assets';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { useNavigate } from 'react-router-dom';
import RetailerListMap from './components/RetailerListMap';
import { RetailerListModel, RetailerHeaderModel, UserFranchiseList } from '../../../app/services/api/ofdClient';
import { UserContext } from '../../../components/shared/useUser';

const ManageRetailers = () => {
  const { getRetailersList } = useNswagClient();
  const [retailers, setRetailers] = useState<RetailerHeaderModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation('manageRetailers');
  const { logError } = useLogError();
  const navigate = useNavigate();
  const [transformedData, setTransformedData] = useState<RetailerHeaderModel[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { user, hasPermissionTo, selectedFranchise } = useContext(UserContext);
  const [page, setPage] = useState(0);
  const [regionTab, setRegionTab] = useState<'my' | 'other'>('my');

  const getUserFranchiseList = (otherRegions: boolean): UserFranchiseList => ({
    franchiseNames: (user?.franchises?.map(f => f.franchiseName).filter((name): name is string => !!name)) ?? [],
    otherRegions,
    currentFranchiseName: selectedFranchise?.franchiseName ?? '',
  });

  useEffect(() => {
    loadRetailerData(regionTab === 'other');
  }, [regionTab]);


  const loadRetailerData = (otherRegions = false) => {
    setIsLoading(true);
    setPage(0);
    getRetailersList(getUserFranchiseList(otherRegions)).then((result: RetailerListModel) => {
      if (result.retailerList) {
        setRetailers(result.retailerList as RetailerHeaderModel[] ?? []);
        setTransformedData(result.retailerList as RetailerHeaderModel[] ?? []); // update map data as well
      }
    })
      .catch((error) => {
        logError(error);
      })
      .finally(() => (setIsLoading(false)));
  };

  return <PageArea>
    <Grid container>
      <Stack width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
        }}>
        <PageTitle subTitle={t('subtitle')}>{t('heading')}</PageTitle>
        <Button variant="primary"
          size="lg"
          startIcon={<SvgIcon> <Plus fill='white' /> </SvgIcon>}
          disabled={!hasPermissionTo(['RetailersCreate'])}
          onClick={() => navigate('/retailers/0')}>
          {t('newRetailer')}
        </Button>
      </Stack>
      {hasPermissionTo(['RetailersViewOtherRegion']) && 
      <Box sx={{ width: '100%', mb: 1, mt: 2 }}>
        <Tabs
          value={regionTab}
          onChange={(_, v) => setRegionTab(v)}
          aria-label="region tabs"
        >
          <Tab label={t('myRegions')}
            value="my" />
          <Tab label={t('otherRegions')}
            value="other" />
        </Tabs>
      </Box>
      }
      <Box sx={{ width: '100%', height: '400px', pt: '12px' }}>
        <RetailerListMap
          retailers={transformedData} />
      </Box>
      <ManageRetailersBox
        onCreate={() => navigate('/retailers/0')}
        retailers={retailers}
        transformedData={transformedData}
        setTransformedData={(retailers: RetailerHeaderModel[]) => setTransformedData(retailers)}
        loadData={() => loadRetailerData(regionTab === 'other')}
        isLoading={isLoading}
        page={page}
        setPage={setPage}
      />
    </Grid>
  </PageArea>;
};

export default ManageRetailers;